import React, { useState, useEffect } from "react";
import { Drawer, List, Stack, Box, Divider, Typography } from "@mui/material";
import { corGeral } from "../../../Styles/cores";
import LogoTelegen from "../../../Assets/Img/Telegen/logo-telegen.png";
import ListItemButton from "./ListItemButton";
import CustomCollapse from "./CustomCollapse";
import { useNavigate, useLocation } from "react-router-dom";
import { routes } from "../../../Constants/routes";
import { clearAuthData } from "../../../utils/userSession";
import {
  AssessmentOutlined,
  NotificationsOutlined,
  WorkOutline,
  AddCircleOutline,
  ListAltOutlined,
  CalendarTodayOutlined,
  PeopleOutlined,
  BuildOutlined,
  PersonOutline,
  LogoutOutlined,
} from "@mui/icons-material";

interface ISideMenuProps {
  isOpen: boolean;
  handleDrawerToggle: () => void;
}

const SideMenu: React.FC<ISideMenuProps> = ({ isOpen, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const isActive = (path: string) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const currentPath = location.pathname;
    
    return currentPath === normalizedPath;
  };

  // Verifica se alguma rota filha de "Ordens de serviço" está ativa
  const isOrdensServicoActive = () => {
    const currentPath = location.pathname;
    
    const osBaseRoutes = [
      '/os',
      '/os/create',
      '/os/calendar',
    ];
    
    if (osBaseRoutes.some(route => currentPath === route)) {
      return true;
    }
    
    if (currentPath.startsWith('/os/') && 
        !currentPath.startsWith('/os/avaliacao') &&
        !currentPath.startsWith('/os/calendar')) {
      const pathParts = currentPath.split('/');
      if (pathParts.length >= 3 && pathParts[1] === 'os') {
        return true;
      }
    }
    
    return false;
  };

  // Abre automaticamente a seção se uma rota filha estiver ativa
  useEffect(() => {
    const currentPath = location.pathname;
    const osBaseRoutes = ['/os', '/os/create', '/os/calendar'];
    const isActive = osBaseRoutes.some(route => currentPath === route) ||
      (currentPath.startsWith('/os/') && 
       !currentPath.startsWith('/os/avaliacao') &&
       !currentPath.startsWith('/os/calendar') &&
       currentPath.split('/').length >= 3);
    
    if (isActive) {
      setOpenSection("ordens-de-servico");
    }
  }, [location.pathname]);

  const handleSectionToggle = (sectionId: string) => {
    setOpenSection((prevSection) =>
      prevSection === sectionId ? null : sectionId
    );
  };

  return (
    <Drawer
      open={isOpen}
      onClose={handleDrawerToggle}
      sx={{
        zIndex: 2200,
        '& .MuiDrawer-paper': {
          backgroundColor: corGeral.navyBlue,
          backgroundImage: 'linear-gradient(180deg, #001C4E 0%, #002855 100%)',
          color: "white",
          minWidth: "280px",
          width: "280px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "4px 0 20px rgba(0, 0, 0, 0.3)",
        },
      }}
    >
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center",
          padding: "32px 20px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <img
          src={LogoTelegen}
          alt="Logo Telegen"
          style={{ 
            maxWidth: "180px",
            height: "auto",
            filter: "brightness(1.1)",
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", padding: "16px 12px" }}>
        <List sx={{ padding: 0 }}>
          <Stack gap={0.5}>
            <ListItemButton
              label="Avaliações"
              onClick={() => navigate(routes.OS_AVALIACAO_LIST)}
              icon={<AssessmentOutlined />}
              isActive={location.pathname.startsWith('/os/avaliacao') || location.pathname === '/os/avaliacao'}
            />
            <ListItemButton
              label="Notificações"
              onClick={() => navigate(routes.NOTIFICACAO_LIST)}
              icon={<NotificationsOutlined />}
              isActive={isActive(routes.NOTIFICACAO_LIST)}
            />
            
            <CustomCollapse
              label="Ordens de serviço"
              onClick={() => handleSectionToggle("ordens-de-servico")}
              isOpen={openSection === "ordens-de-servico"}
              icon={<WorkOutline />}
              isActive={isOrdensServicoActive()}
            >
              <ListItemButton
                label="Criar OS"
                onClick={() => navigate(routes.OS_CREATE)}
                icon={<AddCircleOutline />}
                isActive={isActive(routes.OS_CREATE)}
                isNested
              />
              <ListItemButton
                label="Listar OS"
                onClick={() => navigate(routes.OS_LIST)}
                icon={<ListAltOutlined />}
                isActive={isActive(routes.OS_LIST)}
                isNested
              />
              <ListItemButton
                label="Calendário"
                onClick={() => navigate(routes.OS_CALENDAR)}
                icon={<CalendarTodayOutlined />}
                isActive={isActive(routes.OS_CALENDAR)}
                isNested
              />
            </CustomCollapse>

            <ListItemButton
              label="Clientes"
              onClick={() => navigate(routes.CLIENTE_LIST)}
              icon={<PeopleOutlined />}
              isActive={location.pathname.startsWith('/cliente')}
            />
            <ListItemButton
              label="Equipamentos"
              onClick={() => navigate(routes.EQUIPAMENTO_LIST)}
              icon={<BuildOutlined />}
              isActive={location.pathname.startsWith('/equipamento')}
            />
            <ListItemButton
              label="Usuários"
              onClick={() => navigate(routes.USUARIO_LIST)}
              icon={<PersonOutline />}
              isActive={location.pathname.startsWith('/usuario')}
            />
          </Stack>
        </List>
      </Box>

      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)", margin: "8px 16px" }} />
      
      <Box 
        onClick={() => {
          clearAuthData();
          navigate("/");
        }} 
        sx={{ 
          cursor: "pointer", 
          padding: "16px 20px",
          margin: "8px 12px 16px 12px",
          borderRadius: "12px",
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            transform: "translateX(4px)",
          },
        }}
      >
        <LogoutOutlined sx={{ fontSize: "20px" }} />
        <Typography 
          sx={{ 
            fontWeight: 500,
            fontSize: "15px",
            letterSpacing: "0.3px",
          }}
        >
          Sair
        </Typography>
      </Box>
    </Drawer>
  );
};

export default SideMenu;
